/* eslint-disable complexity */
import { InteractionInput } from '../../InteractionInput';
import { Button } from '@scale/scaleui';
import {
  InteractionStepType,
  ReferenceText,
  Attachment,
  ChatAttachment,
  MultiTurnContinueStepResponse,
  IndexedInteractionStepResponseForType,
  MessageRole,
  AutoFeedbackChatResponse,
} from '@scale/shared/types/chat';
import { extractPrefilledMessages } from '@scale/shared/chat/response/validation';
import { isStepVisible, isConditionallyVisible } from '@scale/shared/chat/api';
import { useMemo, useEffect, useState, useContext, memo } from 'react';
import { useTrans } from '../../hooks/useTrans';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import {
  useChatStoreExtract,
  useMostRecentOutputFullObject,
  useMostRecentPrompt,
  useMostRecentResponse,
  useMostRecentResponseForStepType,
  useMostRecentResponseFullObject,
  useNumberOfTurns,
  useStepParams,
  useStepResponse,
  useTurnByIndex,
} from '../../../Chat/hooks/ChatStore.hooks';
import _ from 'lodash';
import { useStepSubmitHandler } from '../../hooks/useStepSubmitHandler';
import { StepWidthWrapper } from '../../step/StepWidthWrapper';
import { StepViewComponent } from '../stepDescriptor/types';
import { memoizeWithArgs } from 'proxy-memoize';
import { ChatStoreState } from '../../../Chat/hooks/types';
import useEvaluatePromptText, { evaluatePromptText } from '../../hooks/useEvaluatePromptText';
import useEvaluateEndSessionCondition from '../../hooks/useEvaluateEndSessionCondition';
import ModelPromptSelector from 'react-scale/components/scalers/tasks/ThreadedInteraction/ModelPromptSelector';

export const MultiTurnContinueViewComponent: StepViewComponent = ({
  readOnly,
  complete,
  responseIndex,
}) => {
  const taskContext = useContext(ChatTaskContext);
  const config = useChatStoreExtract(state => state.config);
  const responses = useChatStoreExtract(state => state.responses);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const stepParams = useStepParams<InteractionStepType.MultiTurnContinue>(responseIndex);
  const lastMessage = useMostRecentResponse(responseIndex);
  const lastModelResponse = useMostRecentResponseForStepType(
    InteractionStepType.ModelResponseSelector,
    responseIndex,
  );
  const response = useStepResponse<MultiTurnContinueStepResponse>(responseIndex);
  const mostRecentPrompt = useMostRecentPrompt(responseIndex);
  const numTurns = useNumberOfTurns(responseIndex);
  const turnStepParams = useTurnByIndex(numTurns - 1);

  const canEndSession = numTurns >= Number(config.minTurns ?? 1);
  const canContinueSession = numTurns < Number(config.maxTurns ?? 1);

  const isIncompleteStep = !response && !complete;

  // A more robust, backwards-compatible way to stop it from continuing
  const anyPreviousStepTriggersEndOfConvo = useChatStoreExtract(state =>
    state.responses.some(response => response.context?.triggerEndOfConvo === true),
  );
  const previousStep = useMostRecentOutputFullObject(responseIndex);
  const previousStepShouldNotContinue = _.get(previousStep, 'context.shouldContinue') === false;

  // Jsonata hooks
  const {
    evaluatedPromptText: evaluatedDefaultUserMessage,
    isEvaluatingPrompt: isEvaluatingDefaultUserMessage,
  } = useEvaluatePromptText(config.defaultUserMessage, responses, stepParams);
  const { evaluatedEndSessionCondition, isEvaluatingEndSessionCondition } =
    useEvaluateEndSessionCondition(config.endSessionCondition, responses, stepParams);

  /*
   * shouldEndSession supersedes all other automatic submissions (default user message, PS autosubmit, initial messages) via useEffect
   * If any of the conditions in shouldEndSession are met, we should end the session and never auto-submit any type of prompt
   * Note: The behavior of automatic submission is not guaranteed if you have multiple configurations enabled (for example, default user message and initial messages)
   *
   * Note: JSONata and PS end session conditions override the minimum turn check
   * If a minimum number of turns is required, it should be included in the JSONata end session condition
   * TODO: Process Supervision (PS) conditions might be able to break minimum turn checks
   */
  const shouldEndSession =
    !canContinueSession ||
    previousStepShouldNotContinue ||
    anyPreviousStepTriggersEndOfConvo ||
    evaluatedEndSessionCondition;

  const { onSubmit } = useStepSubmitHandler({
    responseIndex,
  });

  // TODO: Introduce type checking when constructing params
  const { t } = useTrans();

  const lastModelResponseContext = lastModelResponse?.context;
  const lastModelSelectionIndex = lastModelResponseContext?.selectedIndex;
  const modelResponseAttachments: ChatAttachment[] | undefined = !_.isNil(lastModelSelectionIndex)
    ? lastModelResponseContext?.candidates?.[lastModelSelectionIndex]?.attachments
    : undefined;

  // Some response generator steps (like ModelResponseEditor) can edit the message metadata
  // When that happens, we need to pull the metadata from the most recent response
  const lastResponseGeneratorObject = useMostRecentResponseFullObject(responseIndex);
  const modelResponseMetadata =
    lastResponseGeneratorObject?.context?.messageMetadata ||
    lastModelResponse?.context?.candidates[0]?.message?.messageMetadata ||
    undefined;

  // These are hacks for now for Lazarus and Kratos, we will follow up with a more general solution
  // https://linear.app/scale-epd/issue/OPAP-1632/come-up-with-better-way-to-auto-skip-user-prompts
  const shouldAutoskip =
    (config.autoSkipAfterToolCall && lastResponseGeneratorObject?.context?.hasToolCalls) ||
    (config.autoSkipAfterToolResponse &&
      (modelResponseMetadata?.role === 'ROLE_TOOL' ||
        modelResponseMetadata?.source === 'ipython')) ||
    lastResponseGeneratorObject?.context?.skipPromptInput;

  const { taskingUserMessages: unevaluatedTaskingUserMessages } = useMemo(() => {
    return extractPrefilledMessages(config.initialMessages);
  }, [config.initialMessages]);

  const initialReferenceTexts = useMemo(() => {
    if (unevaluatedTaskingUserMessages.length <= numTurns) {
      return [];
    }

    const { referenceText, referenceUrl } = unevaluatedTaskingUserMessages[numTurns];
    if (referenceText) {
      return [
        {
          content: referenceText,
          url: referenceUrl,
        },
      ];
    }

    return [];
  }, [unevaluatedTaskingUserMessages, numTurns]);

  const initialAttachments = useMemo(() => {
    if (unevaluatedTaskingUserMessages.length <= numTurns) {
      return [];
    }

    const { promptAttachments } = unevaluatedTaskingUserMessages[numTurns];
    if (promptAttachments) {
      return promptAttachments;
    }

    return [];
  }, [unevaluatedTaskingUserMessages, numTurns]);

  const [referenceTexts, setReferenceTexts] = useState<ReferenceText[]>(
    response?.context?.referenceTexts || initialReferenceTexts,
  );
  const [attachments, setAttachments] = useState<Attachment[]>(
    response?.context?.attachments || initialAttachments,
  );
  const [role, setRole] = useState<MessageRole>(response?.context?.role || MessageRole.User);
  const [selectedPrompt, setSelectedPrompt] = useState<string>('');
  const [displaySuggestionText, setDisplaySuggestionText] = useState<boolean>(
    !!config.isPromptGenerationEnabled,
  );

  // Logic for determining if we want to auto-submit because a Process Supervision prompt is still being solved (that is, isComplete is false)
  const { shouldAutosubmit, multiturnContinueOutput } = useShouldAutosubmitForStepTypes(
    [
      InteractionStepType.ProcessSupervision,
      InteractionStepType.ToolUseProcessSupervision,
      InteractionStepType.SpanFeedback,
    ],
    responseIndex,
  );

  const onSubmitForContinueSession = async (
    prompt: string,
    autoCritiqueResponse: string,
    _autoFeedbackResponse?: AutoFeedbackChatResponse,
    skipPromptInput?: boolean,
  ) => {
    return onSubmit(
      {
        type: InteractionStepType.MultiTurnContinue,
        context: {
          // Data associated with previous assistant turn
          message: lastMessage,
          modelResponseAttachments,
          modelResponseMetadata,
          // Data associated with the current user turn
          shouldContinue: true,
          referenceTexts,
          attachments,
          autoCritiqueResponse,
          role,
          skipPromptInput,
        },
        output: prompt,
      },
      responseIndex,
      stepParams,
    );
  };

  const endSessionStep = useMemo(() => {
    return {
      type: InteractionStepType.MultiTurnContinue,
      context: {
        message: lastMessage,
        shouldContinue: false,
      },
      output: '',
    } as const;
  }, [lastMessage]);

  // Automatically submits a response if a default user message is being used
  useEffect(() => {
    if (isEvaluatingDefaultUserMessage || isEvaluatingEndSessionCondition || shouldEndSession) {
      return;
    }

    if (config.useDefaultUserMessage && canContinueSession && isIncompleteStep) {
      // If all per-turn steps are skipped, we should not iterate through another turn
      const anyStepsVisible = turnStepParams.some(
        step =>
          step.type !== InteractionStepType.MultiTurnContinue &&
          isConditionallyVisible(step, responses, step.responseIndex, undefined, reviewLevel) &&
          isStepVisible(step, reviewLevel),
      );
      onSubmit(
        {
          type: InteractionStepType.MultiTurnContinue,
          context: {
            message: lastMessage,
            shouldContinue: anyStepsVisible,
            referenceTexts,
            attachments,
            modelResponseAttachments,
            role,
            modelResponseMetadata,
          },
          output: config.usePreviousUserMessage
            ? mostRecentPrompt
            : evaluatedDefaultUserMessage ?? '',
        },
        responseIndex,
        stepParams,
        undefined,
        true /* skip linting */,
      );
    }
  }, [
    shouldEndSession,
    isEvaluatingDefaultUserMessage,
    evaluatedEndSessionCondition,
    isEvaluatingEndSessionCondition,
    canEndSession,
    config.useDefaultUserMessage,
    canContinueSession,
    isIncompleteStep,
    turnStepParams,
    responses,
    reviewLevel,
    onSubmit,
    lastMessage,
    referenceTexts,
    attachments,
    modelResponseAttachments,
    role,
    config.usePreviousUserMessage,
    mostRecentPrompt,
    evaluatedDefaultUserMessage,
    responseIndex,
    stepParams,
    modelResponseMetadata,
  ]);

  // Automatically submits a response if a previous step triggers auto submission
  useEffect(() => {
    if (isEvaluatingEndSessionCondition || shouldEndSession) {
      return;
    }

    if ((shouldAutoskip || shouldAutosubmit) && canContinueSession && isIncompleteStep) {
      // If all per-turn steps are skipped, we should not iterate through another turn
      const anyStepsVisible = turnStepParams.some(
        step =>
          step.type !== InteractionStepType.MultiTurnContinue &&
          isConditionallyVisible(step, responses, step.responseIndex, undefined, reviewLevel) &&
          isStepVisible(step, reviewLevel),
      );
      onSubmit(
        {
          type: InteractionStepType.MultiTurnContinue,
          context: {
            message: lastMessage,
            shouldContinue: anyStepsVisible,
            referenceTexts,
            attachments,
            modelResponseAttachments,
            role,
            modelResponseMetadata,
            skipPromptInput: shouldAutoskip,
          },
          output: multiturnContinueOutput,
        },
        responseIndex,
        stepParams,
        undefined,
        true /* skip linting */,
      );
    }
  }, [
    shouldEndSession,
    evaluatedEndSessionCondition,
    isEvaluatingEndSessionCondition,
    canEndSession,
    shouldAutosubmit,
    canContinueSession,
    isIncompleteStep,
    turnStepParams,
    responses,
    reviewLevel,
    onSubmit,
    lastMessage,
    referenceTexts,
    attachments,
    modelResponseAttachments,
    role,
    multiturnContinueOutput,
    responseIndex,
    stepParams,
    modelResponseMetadata,
    shouldAutoskip,
  ]);

  // Automatically submits a response based on the initial messages
  useEffect(() => {
    if (isEvaluatingEndSessionCondition || shouldEndSession) {
      return;
    }

    const setInitialMessages = async () => {
      const taskingUserMessages = await Promise.all(
        unevaluatedTaskingUserMessages.map(async message => ({
          ...message,
          content: await evaluatePromptText(message.content, responses, stepParams),
        })),
      );

      const turnIndex = numTurns;
      if (
        taskingUserMessages.length > turnIndex &&
        taskingUserMessages[turnIndex].content &&
        taskingUserMessages[turnIndex].content!.length > 0 &&
        canContinueSession &&
        isIncompleteStep
      ) {
        const context = {
          message: lastMessage,
          shouldContinue: true,
          referenceTexts,
          attachments,
          modelResponseAttachments,
          role,
          modelResponseMetadata,
        };

        onSubmit(
          {
            type: InteractionStepType.MultiTurnContinue,
            context,
            output: taskingUserMessages[turnIndex].content!,
            role,
          },
          responseIndex,
          stepParams,
          undefined,
          true /* skip linting */,
        );
      }
    };
    setInitialMessages();
  }, [
    shouldEndSession,
    evaluatedEndSessionCondition,
    isEvaluatingEndSessionCondition,
    canEndSession,
    unevaluatedTaskingUserMessages,
    responses,
    numTurns,
    canContinueSession,
    isIncompleteStep,
    lastMessage,
    referenceTexts,
    attachments,
    modelResponseAttachments,
    role,
    onSubmit,
    responseIndex,
    stepParams,
    modelResponseMetadata,
  ]);

  // Automatically submits a response if the max turns is reached or if the end session condition is met
  useEffect(() => {
    if (isEvaluatingEndSessionCondition) {
      return;
    }

    if (shouldEndSession && isIncompleteStep) {
      onSubmit(endSessionStep, responseIndex, stepParams);
    }
  }, [
    evaluatedEndSessionCondition,
    isEvaluatingEndSessionCondition,
    canContinueSession,
    shouldEndSession,
    isIncompleteStep,
    onSubmit,
    endSessionStep,
    responseIndex,
    stepParams,
  ]);

  const enableRTLText = (
    config.before.find(step => step.type === InteractionStepType.PromptInput) as any
  )?.params.enable_rtl_text;

  const turnLimitText = () => {
    if (!config.maxTurns || !config.minTurns) {
      return null;
    } else {
      if (numTurns <= Number(config.minTurns)) {
        return `${numTurns} / ${config.minTurns} required turns`;
      }
      return `${numTurns} / ${config.maxTurns} max turns`;
    }
  };

  if (readOnly || complete || response) {
    return null; // TODO: Should be able to undo from here.
  }

  if (isEvaluatingDefaultUserMessage || isEvaluatingEndSessionCondition) {
    return null;
  }

  const getModelPromptSelector = () => {
    // Only show prompt suggestions if they're being generated in task. Pre-seeded prompt suggestions are only supported for the first turn.
    if (config.isPromptGenerationEnabled && !config.isPreSeededPromptSuggestionEnabled) {
      return (
        <ModelPromptSelector
          responseIndex={responseIndex}
          projectId={taskContext.projectId}
          chatBenchmarkConfig={taskContext.chatBenchmarkConfig}
          complete={complete}
          readOnly={readOnly}
          referenceTexts={referenceTexts}
          attachments={attachments}
          setSelectedPrompt={setSelectedPrompt}
          setDisplaySuggestionText={setDisplaySuggestionText}
        />
      );
    }
    return null;
  };

  return (
    <StepWidthWrapper>
      <InteractionInput
        subtitle={
          <div className="w-full flex justify-between items-center">
            <div className="flex items-center gap-1">
              <div>{canEndSession ? t('continue-hint-can-end') : t('continue-hint')}</div>
            </div>
            <div>{turnLimitText()}</div>
          </div>
        }
        onSubmit={onSubmitForContinueSession}
        placeholder={`Add your prompt (Turn ${numTurns + 1})`}
        actions={
          <>
            {canEndSession && (
              <Button
                variant="danger"
                onClick={() =>
                  confirm(t('end-session')) && onSubmit(endSessionStep, responseIndex, stepParams)
                }
              >
                End Session
              </Button>
            )}
          </>
        }
        referenceTexts={referenceTexts}
        setReferenceTexts={setReferenceTexts}
        responseIndex={responseIndex}
        attachments={attachments}
        setAttachments={setAttachments}
        enableRTLText={enableRTLText}
        autoFeedbackProps={{
          childId: `${taskContext.assignmentId ?? taskContext.taskId}-${
            InteractionStepType.MultiTurnContinue
          }-${responseIndex}`,
        }}
        role={role}
        setRole={id => setRole(id)}
        modelPromptSelector={getModelPromptSelector()}
        selectedPrompt={selectedPrompt}
        setSelectedPrompt={setSelectedPrompt}
        displaySuggestionText={displaySuggestionText}
        isMultiTurnStep
      />
    </StepWidthWrapper>
  );
};

const memoizedShouldAutosubmitForStepTypes = memoizeWithArgs(
  (state: ChatStoreState, stepTypes: InteractionStepType[], responseIndex: number) => {
    for (const stepType of stepTypes) {
      const responsesUntilIndex = state.responses.slice(0, responseIndex);
      const responses = responsesUntilIndex.filter(
        response => response.type === stepType,
      ) as IndexedInteractionStepResponseForType<typeof stepType>[];

      const mostRecentResponse = responses[responses.length - 1];

      if (mostRecentResponse) {
        const isComplete = mostRecentResponse?.context?.isComplete;
        const multiturnContinueOutput =
          mostRecentResponse.context?.multiturnContinueOutput ||
          'The model will now continue to respond to the prompt.';

        if (isComplete === false) {
          return {
            shouldAutosubmit: true,
            mostRecentResponse,
            multiturnContinueOutput,
          };
        }
      }
    }

    return {
      shouldAutosubmit: false,
      mostRecentResponse: null,
      multiturnContinueOutput: '',
    };
  },
);

// Function to handle auto-submission logic for process supervision types (but can be broadly used)
// if isComplete is passed as false, we should autosubmit
const useShouldAutosubmitForStepTypes = (
  stepTypes: InteractionStepType[],
  responseIndex: number,
) => {
  return useChatStoreExtract(state =>
    memoizedShouldAutosubmitForStepTypes(state, stepTypes, responseIndex),
  );
};

export default memo(MultiTurnContinueViewComponent);
